import { ComponentProps, useMemo } from 'react';

import { ActionButton, ModalFooter } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { WidgetConfigModalProps } from '@/types.ts';
import { Icon } from '@/utils';

type ModalFooterActionButtons = NonNullable<ComponentProps<typeof ModalFooter>['actionButtons']>;

type WidgetConfigModalFooterProps = Pick<WidgetConfigModalProps, 'onClose' | 'onGoBack'> & {
  /** Prepended before close/back (e.g. save on form-based config modals). */
  leadingActionButtons?: readonly ActionButton[];
};

export const WidgetConfigModalFooter = ({
  onClose,
  onGoBack,
  leadingActionButtons,
}: WidgetConfigModalFooterProps) => {
  const { t } = useTranslation();

  const actionButtons = useMemo((): ModalFooterActionButtons | undefined => {
    const closeButton: ActionButton = {
      label: t('close'),
      onClick: onClose ?? (() => undefined),
    };
    const backButton: ActionButton = {
      label: t('back'),
      onClick: onGoBack,
      headIcon: Icon.ChevronLeft,
    };

    const trailingButtons: ActionButton[] = onGoBack
      ? [closeButton, backButton]
      : onClose
        ? [closeButton]
        : [];

    const buttons = [...(leadingActionButtons ?? []), ...trailingButtons];
    if (buttons.length === 0) return undefined;

    return buttons as ModalFooterActionButtons;
  }, [leadingActionButtons, onClose, onGoBack, t]);

  return <ModalFooter actionButtons={actionButtons} />;
};
